import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    AppBar,
    Toolbar,
    Stack,
    Link,
    useTheme,
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';

/** Statusense-aligned green palette */
const palette = {
    navy: '#0d3a31',
    navyMid: '#145245',
    paper: '#f4fbf8',
    subtle: '#dcefe7',
    text: '#1f3a33',
    textMuted: '#44665d',
    border: '#bdd8cd',
    accent: '#1f8a70',
    accentDeep: '#166a56',
};

const DEMO_EMAIL = 'info@insightsplex.com';
const FOOTER_CONTACT_EMAIL = 'info@insightsplex.com';

const problemSignals = [
    'Signals scattered across tools',
    'RAG status masking trajectory shifts',
    'Escalations reaching leadership unexpectedly',
    'Margin erosion discovered after the fact',
    'Increasing review overhead without better insight',
];

const assuranceCapabilities = [
    {
        icon: <HubOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Integrates data across multiple systems',
    },
    {
        icon: <FunctionsOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Computes standardized KPIs',
    },
    {
        icon: <GavelOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Institutionalizes Risks, Assumptions, Issues & Dependencies',
    },
    {
        icon: <AssignmentOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Enforces consistent governance through structured questionnaires',
    },
    {
        icon: <TrackChangesOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Surfaces early warning signals in real time',
    },
    {
        icon: <DashboardOutlinedIcon sx={{ fontSize: 26 }} aria-hidden />,
        text: 'Provides transparency through dashboards',
    },
];

const leaderOutcomes = [
    {
        title: 'Reduce Surprises',
        body: 'Detect drift early and intervene proactively.',
        icon: <TrendingUpOutlinedIcon sx={{ fontSize: 32, color: palette.accent }} aria-hidden />,
    },
    {
        title: 'Protect Economics',
        body: 'Minimize cost overruns and protect margins.',
        icon: <SavingsOutlinedIcon sx={{ fontSize: 32, color: palette.accent }} aria-hidden />,
    },
    {
        title: 'Strengthen Trust',
        body: 'Improve stakeholder confidence and customer loyalty.',
        icon: <HandshakeOutlinedIcon sx={{ fontSize: 32, color: palette.accent }} aria-hidden />,
    },
];

const deliveryEnvironments = [
    {
        icon: <ApartmentOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
        text: 'IT Services Organizations',
    },
    {
        icon: <GroupsOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
        text: 'Global Capability Centers (GCCs)',
    },
    {
        icon: <TerminalOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
        text: 'Enterprise Technology Teams',
    },
    {
        icon: <CorporateFareOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
        text: 'Portfolio Governance Offices',
    },
];

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    const requestDemoHref = `mailto:${DEMO_EMAIL}`;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: palette.paper,
                color: palette.text,
                fontFamily: theme.typography.fontFamily,
            }}
        >
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(8px)',
                    borderBottom: `1px solid ${palette.border}`,
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', maxWidth: 1200, width: '100%', mx: 'auto' }}>
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="Statusense Logo"
                        sx={{ height: { xs: 34, sm: 40 }, width: 'auto', objectFit: 'contain' }}
                    />
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="text" color="inherit" onClick={() => navigate('/login')} sx={{ color: palette.text }}>
                            Sign in
                        </Button>
                        <Button
                            variant="contained"
                            component="a"
                            href={requestDemoHref}
                            sx={{ bgcolor: palette.accent, '&:hover': { bgcolor: palette.accentDeep } }}
                        >
                            Request a Demo
                        </Button>
                    </Stack>
                </Toolbar>
            </AppBar>

            {/* Hero — copy from Home Page inputs.pdf */}
            <Box
                sx={{
                    background: `linear-gradient(165deg, ${palette.navy} 0%, ${palette.navyMid} 55%, #1a3a5c 100%)`,
                    color: '#f0f4f8',
                    pt: { xs: 6, md: 10 },
                    pb: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(ellipse 80% 50% at 100% 0%, rgba(255,255,255,0.06) 0%, transparent 55%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography
                        variant="overline"
                        sx={{ color: 'rgba(240,244,248,0.75)', letterSpacing: '0.14em', fontWeight: 600, display: 'block', mb: 2 }}
                    >
                        Project Delivery Assurance
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.75rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            letterSpacing: '-0.03em',
                            mb: 2,
                        }}
                    >
                        Turn Governance into Execution Assurance.
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '1.2rem', md: '1.35rem' },
                            fontWeight: 500,
                            lineHeight: 1.45,
                            color: 'rgba(240,244,248,0.92)',
                            mb: 1.5,
                        }}
                    >
                        Detect Delivery Drift before it becomes Escalation.
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '1.1rem', md: '1.2rem' },
                            fontWeight: 400,
                            lineHeight: 1.5,
                            color: 'rgba(240,244,248,0.88)',
                            mb: 4,
                        }}
                    >
                        From Status Reporting to Predictable Outcomes
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            maxWidth: 640,
                            color: 'rgba(240,244,248,0.78)',
                            lineHeight: 1.75,
                            mb: 4,
                            fontSize: '1.05rem',
                        }}
                    >
                        Statusense integrates execution data across systems into a governed intelligence layer — surfacing early
                        warning signals, enhancing predictability, and reducing surprise escalations.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        component="a"
                        href={requestDemoHref}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1.05rem',
                            bgcolor: '#fff',
                            color: palette.accentDeep,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                            '&:hover': { bgcolor: '#f0f4f8' },
                        }}
                    >
                        Request a Demo
                    </Button>
                </Container>
            </Box>

            {/* Problem framing */}
            <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
                <Grid container spacing={6} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h2" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 700, color: palette.accentDeep, mb: 2, lineHeight: 1.35 }}>
                            Most Governance Systems Detect Risk Too Late
                        </Typography>
                        <Stack component="ul" spacing={1.75} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                            {problemSignals.map((line) => (
                                <Box
                                    component="li"
                                    key={line}
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'flex-start',
                                        pl: 0,
                                    }}
                                >
                                    <ErrorOutlineRoundedIcon sx={{ color: '#b91c1c', fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                                    <Typography sx={{ color: palette.text, lineHeight: 1.65 }}>{line}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                borderLeft: `4px solid ${palette.accent}`,
                                pl: 3,
                                py: 1,
                            }}
                        >
                            <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 600, color: palette.accentDeep, mb: 1.5 }}>
                                Projects rarely fail suddenly.
                            </Typography>
                            <Typography sx={{ fontSize: '1.15rem', fontWeight: 500, color: palette.text, lineHeight: 1.5 }}>
                                They drift silently.
                            </Typography>
                        </Box>
                        <Typography sx={{ mt: 4, color: palette.textMuted, lineHeight: 1.8, fontSize: '1.02rem' }}>
                            <Box component="strong" sx={{ color: palette.text, fontWeight: 600 }}>
                                Visibility Is Not the Same as Predictability
                            </Box>{' '}
                            Most organizations have dashboards and reviews. What they lack is early detection of trajectory change.
                            Statusense connects execution signals, institutionalizes risk management, and surfaces patterns before
                            thresholds are breached.
                        </Typography>
                    </Grid>
                </Grid>
            </Container>

            {/* Platform */}
            <Box sx={{ bgcolor: '#fff', borderTop: `1px solid ${palette.subtle}`, borderBottom: `1px solid ${palette.subtle}` }}>
                <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.5rem', md: '1.85rem' },
                            fontWeight: 700,
                            color: palette.accentDeep,
                            textAlign: 'center',
                            mb: 1,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        A Project Delivery Assurance Platform
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: palette.textMuted,
                            maxWidth: 560,
                            mx: 'auto',
                            mb: 5,
                            lineHeight: 1.7,
                        }}
                    >
                        One layer for governed insight across your delivery ecosystem.
                    </Typography>
                    <Grid container spacing={2.5}>
                        {assuranceCapabilities.map((row) => (
                            <Grid item xs={12} sm={6} key={row.text}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                        p: 2.5,
                                        height: '100%',
                                        border: `1px solid ${palette.subtle}`,
                                        borderRadius: 1,
                                        bgcolor: palette.paper,
                                        transition: 'box-shadow 0.2s',
                                        '&:hover': { boxShadow: '0 8px 24px rgba(15, 35, 60, 0.06)' },
                                    }}
                                >
                                    <Box sx={{ color: palette.accent, flexShrink: 0, mt: 0.25 }}>{row.icon}</Box>
                                    <Typography sx={{ color: palette.text, lineHeight: 1.65, fontSize: '0.98rem' }}>{row.text}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Leaders */}
            <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 700,
                        color: palette.accentDeep,
                        textAlign: 'center',
                        mb: 5,
                        letterSpacing: '-0.02em',
                    }}
                >
                    Designed for Leaders Who Value Predictability
                </Typography>
                <Grid container spacing={3}>
                    {leaderOutcomes.map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <Box
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    border: `1px solid ${palette.border}`,
                                    borderRadius: 1,
                                    bgcolor: '#fff',
                                }}
                            >
                                <Box sx={{ mb: 2 }}>{item.icon}</Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: palette.accentDeep, mb: 1.5 }}>{item.title}</Typography>
                                <Typography sx={{ color: palette.textMuted, lineHeight: 1.7 }}>{item.body}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Environments */}
            <Box sx={{ bgcolor: palette.navy, color: '#e2e8f0', py: { xs: 7, md: 9 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: 4,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Built for Complex Delivery Environments
                    </Typography>
                    <Grid container spacing={2} justifyContent="center">
                        {deliveryEnvironments.map((row) => (
                            <Grid item xs={12} sm={6} key={row.text}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 2,
                                        px: 2.5,
                                        borderRadius: 1,
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        bgcolor: 'rgba(255,255,255,0.04)',
                                    }}
                                >
                                    <Box sx={{ color: '#94a3b8' }}>{row.icon}</Box>
                                    <Typography sx={{ fontSize: '0.98rem', lineHeight: 1.5 }}>{row.text}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Closing CTA */}
            <Box sx={{ py: { xs: 8, md: 11 }, textAlign: 'center', bgcolor: palette.paper }}>
                <Container maxWidth="sm">
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.65rem', md: '2rem' },
                            fontWeight: 700,
                            color: palette.accentDeep,
                            mb: 3,
                            letterSpacing: '-0.03em',
                            lineHeight: 1.25,
                        }}
                    >
                        Stop Reporting. Start Assuring.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                        <Button
                            variant="contained"
                            size="large"
                            component="a"
                            href={requestDemoHref}
                            sx={{ px: 4, py: 1.5, textTransform: 'none', fontWeight: 600, bgcolor: palette.accent, '&:hover': { bgcolor: palette.accentDeep } }}
                        >
                            Request a Demo
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => navigate('/login')}
                            sx={{
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: palette.accent,
                                color: palette.accent,
                                '&:hover': { borderColor: palette.accentDeep, bgcolor: 'rgba(28, 75, 130, 0.04)' },
                            }}
                        >
                            Explore the Sandbox
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: palette.navyMid, color: '#cde4dc', py: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Container maxWidth="lg">
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Box
                                component="img"
                                src="/logo.png"
                                alt="Statusense Logo"
                                sx={{ height: 24, width: 'auto', objectFit: 'contain' }}
                            />
                            <Typography variant="body2" sx={{ color: '#a8cbc0' }}>
                                © {new Date().getFullYear()} Statusense by Insightsplex. All rights reserved.
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#d8ece6', fontWeight: 500 }}>
                                Insightsplex
                            </Typography>
                            <Typography variant="body2">
                                Contact:{' '}
                                <Link href={`mailto:${FOOTER_CONTACT_EMAIL}`} sx={{ color: '#eaf7f3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {FOOTER_CONTACT_EMAIL}
                                </Link>
                            </Typography>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;
